import { Injectable, Injector } from '@angular/core';
import { CacheService } from '@df/core/cache/cache';
import flatten from 'lodash-es/flatten';
import { DfsGlobalsService } from '../core/dfs-globals.service';
import { NDfs } from '../dfs.interfaces';
import { DfsCssService } from '../core/dfs-css.service';
import { DfsConfigParserService, IDfsConfigParserOptions } from './parsers/dfs-config-parser.service';
import { DfsDimensionsParserService } from './parsers/dfs-dimension-parser.service';
import { DfsRatioParserService } from './parsers/dfs-ratio-parser.service';
import { DfsScopeClassParserService } from './parsers/dfs-scope-class-parser.service';
import { DfsSimpleParserService, IDfsSimpleParserOptions } from './parsers/dfs-simple-parser.service';
import { DfsWrapParserService } from './parsers/dfs-wrap-parser.service';

@Injectable({
    providedIn: 'any'
})
export class DfsAtomicService {
    /**
     * Dependencies
     */
    constructor(
        private injector: Injector,
        private cacheService: CacheService,
        private dfsCssService: DfsCssService // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ) {}

    /**
     * Atomic rules set
     */
    protected rules: { [id: string]: NDfs.TRule } = {
        // margin
        'm-t': {
            prop: 'margin-top',
            parser: DfsDimensionsParserService
        },
        'm-b': {
            prop: 'margin-bottom',
            parser: DfsDimensionsParserService
        },
        'm-l': {
            prop: 'margin-left',
            parser: DfsDimensionsParserService
        },
        'm-r': {
            prop: 'margin-right',
            parser: DfsDimensionsParserService
        },
        'm-v': {
            combo: ['m-t', 'm-b']
        },
        'm-h': {
            combo: ['m-l', 'm-r']
        },
        'm-a': {
            combo: ['m-l', 'm-r', 'm-t', 'm-b']
        },
        // padding
        'p-t': {
            prop: 'padding-top',
            parser: DfsDimensionsParserService
        },
        'p-b': {
            prop: 'padding-bottom',
            parser: DfsDimensionsParserService
        },
        'p-l': {
            prop: 'padding-left',
            parser: DfsDimensionsParserService
        },
        'p-r': {
            prop: 'padding-right',
            parser: DfsDimensionsParserService
        },
        'p-v': {
            combo: ['p-t', 'p-b']
        },
        'p-h': {
            combo: ['p-l', 'p-r']
        },
        'p-a': {
            combo: ['p-l', 'p-r', 'p-t', 'p-b']
        },
        // positioning
        top: {
            prop: 'top',
            parser: DfsDimensionsParserService
        },
        left: {
            prop: 'left',
            parser: DfsDimensionsParserService
        },
        right: {
            prop: 'right',
            parser: DfsDimensionsParserService
        },
        bottom: {
            prop: 'bottom',
            parser: DfsDimensionsParserService
        },
        // other
        w: {
            prop: 'width',
            parser: DfsDimensionsParserService
        },
        h: {
            prop: 'height',
            parser: DfsDimensionsParserService
        },
        'min-height': {
            prop: 'min-height',
            parser: DfsDimensionsParserService
        },
        'min-width': {
            prop: 'min-width',
            parser: DfsDimensionsParserService
        },
        'max-height': {
            prop: 'max-height',
            parser: DfsDimensionsParserService
        },
        'max-width': {
            prop: 'max-width',
            parser: DfsDimensionsParserService
        },
        lh: {
            prop: 'line-height',
            parser: DfsDimensionsParserService
        },
        // border width
        'bw-t': {
            prop: 'border-top-width',
            parser: DfsDimensionsParserService
        },
        'bw-b': {
            prop: 'border-bottom-width',
            parser: DfsDimensionsParserService
        },
        'bw-l': {
            prop: 'border-left-width',
            parser: DfsDimensionsParserService
        },
        'bw-r': {
            prop: 'border-right-width',
            parser: DfsDimensionsParserService
        },
        'b-t': {
            combo: ['bw-t', () => ({ prop: 'border-top-style', value: 'solid' })]
        },
        'b-b': {
            combo: ['bw-b', () => ({ prop: 'border-bottom-style', value: 'solid' })]
        },
        'b-l': {
            combo: ['bw-l', () => ({ prop: 'border-left-style', value: 'solid' })]
        },
        'b-r': {
            combo: ['bw-r', () => ({ prop: 'border-right-style', value: 'solid' })]
        },
        'b-v': {
            combo: ['b-t', 'b-b']
        },
        'b-h': {
            combo: ['b-l', 'b-r']
        },
        'b-a': {
            combo: ['bw-l', 'bw-r', 'bw-t', 'bw-b', () => ({ prop: 'border-style', value: 'solid' })]
        },
        br: {
            prop: 'border-radius',
            parser: DfsDimensionsParserService
        },
        // colors
        c: {
            prop: 'color',
            parser: DfsConfigParserService,
            parserOptions: {
                source: 'color'
            }
        },
        bg: {
            prop: 'background-color',
            parser: DfsConfigParserService,
            parserOptions: {
                source: 'color'
            }
        },
        bc: {
            prop: 'border-color',
            parser: DfsConfigParserService,
            parserOptions: <IDfsConfigParserOptions>{
                source: 'color'
            }
        },
        // aspect ratio
        ratio: {
            prop: 'aspect-ratio',
            parser: DfsRatioParserService
        },
        // aspect ratio
        wrap: {
            prop: 'wrap',
            parser: DfsWrapParserService
        },

        // class support
        class: 'className',
        t: {
            prop: 'className',
            parser: DfsScopeClassParserService,
            parserOptions: {
                prefix: true
            }
        },

        fs: 'font-size',
        // @see https://www.w3schools.com/cssref/css3_pr_float.asp
        float: 'float',
        // @see https://www.w3schools.com/cssref/css3_pr_flex.asp
        flex: 'flex',
        // @see https://www.w3schools.com/cssref/css3_pr_align-items.asp
        'flex-align-items': 'align-items',
        // @see https://www.w3schools.com/cssref/css3_pr_flex-direction.asp
        'flex-direction': 'flex-direction',
        // @see https://www.w3schools.com/cssref/css3_pr_justify-content.asp
        'flex-justify-content': 'justify-content',
        // @see https://www.w3schools.com/cssref/css3_pr_flex.asp
        'flex-shrink': 'flex-shrink',
        // @see https://www.w3schools.com/cssref/css3_pr_flex-grow.asp
        'flex-grow': 'flex-grow',
        // @see https://www.w3schools.com/cssref/css3_pr_flex-wrap.asp
        'flex-wrap': 'flex-wrap',
        // @see https://www.w3schools.com/cssref/css3_pr_gap.php
        gap: 'gap',

        // @see https://www.w3schools.com/cssref/pr_class_position.asp
        pos: 'position',
        // @see https://www.w3schools.com/cssref/pr_class_display.asp
        d: {
            prop: 'display',
            parser: DfsSimpleParserService,
            parserOptions: <IDfsSimpleParserOptions>{
                customCss: {
                    fill: { top: '0', position: 'absolute', bottom: '0', left: '0', right: '0', width: '100%', height: '100%' }
                }
            }
        },
        // @see https://www.w3schools.com/cssref/pr_text_text-align.ASP
        ta: 'text-align',
        // @see https://www.w3schools.com/cssref/pr_class_cursor.asp
        cursor: 'cursor',
        // @see https://www.w3schools.com/cssref/pr_pos_overflow.asp
        o: 'overflow',
        // @see https://www.w3schools.com/cssref/css3_pr_overflow-x.asp
        'o-x': 'overflow-x',
        // @see https://www.w3schools.com/cssref/css3_pr_overflow-y.asp
        'o-y': 'overflow-y',
        // @see https://www.w3schools.com/cssref/css3_pr_text-overflow.php
        'text-overflow': 'text-overflow',
        // @see https://www.w3schools.com/cssref/pr_class_visibility.asp
        v: 'visibility',
        // @see https://www.w3schools.com/cssref/pr_text_white-space.asp
        'white-space': 'white-space',
        // @see https://www.w3schools.com/cssref/pr_pos_vertical-align.asp
        va: 'vertical-align',
        // @see https://www.w3schools.com/cssref/pr_text_text-decoration.asp
        td: 'text-decoration',
        // @see https://www.w3schools.com/cssref/pr_z-index.asp
        z: 'z-index'
    };

    /**
     * Parse styles string
     */
    parse(
        dfsGlobalsService: DfsGlobalsService,
        dfs: string | undefined | null | false | (undefined | null | false | string)[]
    ): NDfs.IClassesEntry[] {
        if (!dfs) {
            return [];
        }
        return this.cacheService.get<NDfs.IClassesEntry[]>(['dfs', 'parse', dfsGlobalsService.dfsScopeAttr, dfs], () => {
            if (Array.isArray(dfs)) {
                return flatten(dfs.filter(i => !!i).map(i => this.parse(dfsGlobalsService, i)));
            }
            if (typeof dfs !== 'string') {
                console.error('Invalid dfs entry: ', dfs);
                throw new Error('dfs needs to be string');
            }

            return flatten(
                dfs
                    .replace(/\s*(\+|:|-|\*)\s*/gm, '$1')
                    .trim()
                    .split(/\s+/)
                    .map(dfsPart => {
                        const [key, value] = dfsPart.split(':', 2);
                        const [property, sizeString] = key.split('@', 2);
                        const styles = this.getCss(dfsGlobalsService, property, value);
                        const classes: string[] = [];
                        const directStyles = styles.filter(i => {
                            if (i.prop === 'className') {
                                classes.push(i.value);
                                return false;
                            }
                            return true;
                        });
                        if (directStyles[0]) {
                            classes.push(this.dfsCssService.toClass(property, value, directStyles));
                        }
                        const size = sizeString?.toUpperCase().split('') as ('S' | 'M' | 'L' | 'X')[];
                        return classes.map(className => ({
                            className,
                            size
                        }));
                    })
            );
        })!;
    }

    /**
     * Retrieves css entry for given short code and value
     */
    private getCss(dfsGlobalsService: DfsGlobalsService, prop: string, value: string): NDfs.ICssEntry[] {
        return this.cacheService.get<NDfs.ICssEntry[]>(['dfs', 'getCss', dfsGlobalsService.dfsScopeAttr, prop, value], () => {
            const rule = this.rules[prop];
            if (!rule) {
                throw new Error('Invalid DFS rule: ' + prop);
            }
            const parser = this.getParser(rule);

            if (parser) {
                return parser.parse(dfsGlobalsService, value, this.getRule(rule));
            }
            if ((rule as NDfs.IComboRule).combo) {
                return flatten(
                    (rule as NDfs.IComboRule).combo.map(i => (typeof i === 'string' ? this.getCss(dfsGlobalsService, i, value) : i()))
                );
            }
            throw new Error('Invalid DFS rule type: ' + prop);
        })!;
    }

    private getRule(rule: string | NDfs.TRule): NDfs.TRule {
        if (typeof rule === 'string') {
            return {
                prop: rule,
                parser: DfsSimpleParserService
            };
        }
        return rule;
    }
    /**
     * Retrieves parser for a rule
     */
    private getParser(rule: NDfs.TRule): NDfs.IParser | undefined {
        const parser = (this.getRule(rule) as NDfs.IParserRule).parser;

        return parser && this.injector.get(parser);
    }
}
